import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Linkedin, Instagram, Facebook, Youtube, Loader2,
  CheckCircle2, AlertCircle, Plug, Unplug, ExternalLink, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_META = {
  linkedin: { label: 'LinkedIn', icon: Linkedin, accent: 'bg-sky-50 text-sky-700 border-sky-200', desc: 'Post to your profile feed' },
  instagram: { label: 'Instagram', icon: Instagram, accent: 'bg-pink-50 text-pink-700 border-pink-200', desc: 'Publish to Business accounts' },
  facebook: { label: 'Facebook', icon: Facebook, accent: 'bg-blue-50 text-blue-700 border-blue-200', desc: 'Publish to Pages you manage' },
  youtube: { label: 'YouTube Shorts', icon: Youtube, accent: 'bg-red-50 text-red-700 border-red-200', desc: 'Upload short-form videos' },
  tiktok: { label: 'TikTok', icon: () => <span className="text-xs font-bold leading-none">TT</span>, accent: 'bg-zinc-100 text-zinc-700 border-zinc-300', desc: 'Coming soon — awaiting platform approval', coming_soon: true },
  bluesky: { label: 'Bluesky', icon: () => <span className="text-xs font-bold leading-none">BS</span>, accent: 'bg-sky-50 text-sky-700 border-sky-200', desc: 'Post via AT Protocol — uses an app password, no OAuth redirect' },
};

export const ConnectionsPage = () => {
  const { api } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // platform string
  const [fbPages, setFbPages] = useState([]);
  const [fbSelected, setFbSelected] = useState(null);
  const [fbLoading, setFbLoading] = useState(false);
  const [liOrgs, setLiOrgs] = useState([]);
  const [liSelected, setLiSelected] = useState(null);
  const [liLoading, setLiLoading] = useState(false);
  const [liHint, setLiHint] = useState(null);

  const fetchFacebookPages = useCallback(async () => {
    setFbLoading(true);
    try {
      const res = await api.get('/connections/facebook/pages');
      setFbPages(res.data.pages || []);
      setFbSelected(res.data.selected_page_id || null);
    } catch (_) {
      // silent — Facebook not connected, dropdown won't render
    } finally {
      setFbLoading(false);
    }
  }, [api]);

  const fetchLinkedInOrgs = useCallback(async () => {
    setLiLoading(true);
    try {
      const res = await api.get('/connections/linkedin/organizations');
      setLiOrgs(res.data.organizations || []);
      setLiSelected(res.data.selected_org_urn || null);
      setLiHint(res.data.hint || null);
    } catch (_) {
      // silent — LinkedIn not connected
    } finally {
      setLiLoading(false);
    }
  }, [api]);

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/connections');
      setConnections(res.data.connections || []);
    } catch {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // 'personal' is a sentinel value so the Select can show "Personal profile" as an option
  const handleSelectLiTarget = async (value) => {
    const isPersonal = value === 'personal';
    if ((isPersonal && !liSelected) || value === liSelected) return;
    const orgUrn = isPersonal ? null : value;
    const orgName = isPersonal ? null : (liOrgs.find((o) => o.urn === value)?.name || null);
    const promise = api
      .post('/connections/linkedin/select-organization', { org_urn: orgUrn, org_name: orgName })
      .then((res) => {
        setLiSelected(res.data.org_urn || null);
        fetchConnections();
        return res.data;
      });
    toast.promise(promise, {
      loading: 'Saving LinkedIn target…',
      success: (d) => (d.posting_as === 'company_page'
        ? `Posting as ${d.org_name || 'Company Page'}`
        : 'Posting as your personal profile'),
      error: 'Failed to set LinkedIn target',
    });
  };

  const handleSelectFbPage = async (pageId) => {
    if (!pageId || pageId === fbSelected) return;
    const promise = api.post('/connections/facebook/select-page', { page_id: pageId }).then((res) => {
      setFbSelected(res.data.page_id);
      fetchConnections();
      return res.data;
    });
    toast.promise(promise, {
      loading: 'Saving default Page…',
      success: (d) => `Default Page set to ${d.page_name}`,
      error: 'Failed to set default Page',
    });
  };

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  // Surface the result of the Instagram-Direct OAuth redirect.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const igd = sp.get('ig_direct');
    if (!igd) return;
    if (igd === 'connected') {
      const handle = sp.get('handle');
      toast.success(handle ? `Instagram connected: @${handle}` : 'Instagram connected directly');
      fetchConnections();
    } else if (igd === 'error') {
      toast.error(`Instagram Direct connect failed: ${sp.get('msg') || 'unknown error'}`);
    }
    // Clean the query string so the toast doesn't re-fire on refresh.
    window.history.replaceState({}, '', window.location.pathname);
  }, [fetchConnections]);

  // Auto-fetch FB pages once we know FB is connected
  useEffect(() => {
    const fbConn = connections.find((c) => c.platform === 'facebook');
    if (fbConn?.connected) fetchFacebookPages();
    else { setFbPages([]); setFbSelected(null); }
  }, [connections, fetchFacebookPages]);

  // Auto-fetch LinkedIn orgs once we know LinkedIn is connected
  useEffect(() => {
    const liConn = connections.find((c) => c.platform === 'linkedin');
    if (liConn?.connected) fetchLinkedInOrgs();
    else { setLiOrgs([]); setLiSelected(null); setLiHint(null); }
  }, [connections, fetchLinkedInOrgs]);

  const [bskyModal, setBskyModal] = useState(false);
  const [bskyHandle, setBskyHandle] = useState('');
  const [bskyPassword, setBskyPassword] = useState('');
  const [bskySaving, setBskySaving] = useState(false);

  const startConnect = async (platform) => {
    // Bluesky uses app-password auth, not OAuth redirect.
    if (platform === 'bluesky') {
      setBskyHandle('');
      setBskyPassword('');
      setBskyModal(true);
      return;
    }
    // Instagram Direct (no Facebook) — dedicated Instagram-Login flow.
    if (platform === 'instagram-direct') {
      setBusy('instagram-direct');
      try {
        const res = await api.post('/instagram-direct/start');
        window.location.href = res.data.authorize_url;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to start Instagram Direct connect');
        setBusy(null);
      }
      return;
    }
    setBusy(platform);
    try {
      const res = await api.post(`/connections/${platform}/start`);
      // Persist platform so the callback can complete the flow
      sessionStorage.setItem('oauth_platform', platform);
      sessionStorage.setItem('oauth_state', res.data.state);
      window.location.href = res.data.authorize_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start OAuth');
      setBusy(null);
    }
  };

  const submitBlueskyConnect = async () => {
    if (!bskyHandle.trim() || !bskyPassword.trim()) return;
    setBskySaving(true);
    try {
      const res = await api.post('/connections/bluesky/connect', {
        handle: bskyHandle.trim(),
        app_password: bskyPassword.trim(),
      });
      toast.success(`Connected as @${res.data.handle}`);
      setBskyModal(false);
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bluesky connection failed');
    } finally {
      setBskySaving(false);
    }
  };

  const disconnect = async (platform, connectionId = null, accountName = null) => {
    const target = connectionId
      ? `${PLATFORM_META[platform].label}${accountName ? ` (${accountName})` : ''}`
      : PLATFORM_META[platform].label;
    if (!window.confirm(`Disconnect ${target}?`)) return;
    try {
      if (connectionId) {
        await api.delete(`/connections/by-id/${connectionId}`);
      } else {
        await api.delete(`/connections/${platform}`);
      }
      toast.success('Disconnected');
      fetchConnections();
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const testConnection = async (platform) => {
    setBusy(platform);
    const promise = api.post(`/connections/${platform}/test`).then((res) => {
      const d = res.data;
      if (d.ok) {
        // refresh list to pick up updated account_name
        fetchConnections();
        return d;
      }
      throw new Error(d.error || 'Connection test failed');
    });
    toast.promise(promise, {
      loading: `Pinging ${PLATFORM_META[platform].label}…`,
      success: (d) => `Connected as ${d.account || 'OK'}`,
      error: (err) => err?.message || 'Connection test failed',
    });
    try { await promise; } catch (_) { /* toast already shown */ }
    setBusy(null);
  };

  return (
    <div className="space-y-6" data-testid="connections-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <Plug className="w-6 h-6 text-indigo-600" />
          Connected accounts
        </h1>
        <p className="text-zinc-500 text-sm mt-0.5">
          Connect your social accounts so PostToSocial can publish on your behalf.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {connections.flatMap((c) => {
            const meta = PLATFORM_META[c.platform] || { label: c.platform, icon: Plug, accent: '' };
            const Icon = meta.icon;
            const isMulti = c.supports_multi;
            const instances = c.instances || [];
            const cards = [];

            // For multi-instance platforms (FB/IG), render ONE card per
            // connected account. For single-instance platforms, render one
            // card whether connected or not (legacy shape).
            const seeds = isMulti && instances.length > 0
              ? instances.map((inst) => ({ ...c, ...inst, _isInstance: true }))
              : [{ ...c, ...(instances[0] || {}), _isInstance: false }];

            seeds.forEach((card, idx) => {
              const showFbDefault =
                card.platform === 'facebook' && !isMulti && fbPages.length > 0;
              const showLiTarget = card.platform === 'linkedin';
              const cardKey = card.connection_id || `${card.platform}-${idx}`;
              const cardTestId = card._isInstance && card.connection_id
                ? `connection-card-${card.platform}-${card.connection_id}`
                : `connection-card-${card.platform}`;
              const platformStatus = card.status || 'enabled'; // 'enabled' | 'pending' | 'disabled'
              const isPending = platformStatus === 'pending';
              const isPlatformDisabled = platformStatus === 'disabled';
              const adminCanBypass = Boolean(card.admin_override);
              // Greyed-out look applies when platform isn't enabled AND the caller
              // is not an admin (admin_override === false). Admin still sees the
              // card in normal styling but with the badge so they can test.
              const greyedOut = (isPending || isPlatformDisabled) && !adminCanBypass;
              cards.push(
                <Card
                  key={cardKey}
                  className={`border-zinc-200 ${greyedOut ? 'opacity-60 grayscale' : ''}`}
                  data-testid={cardTestId}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${meta.accent}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      {card.connected ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
                        </Badge>
                      ) : isPending ? (
                        <Badge
                          variant="outline"
                          className="bg-amber-50 text-amber-800 border-amber-200"
                          data-testid={`pending-approval-${card.platform}`}
                        >
                          <AlertCircle className="w-3 h-3 mr-1" /> Pending Approval
                        </Badge>
                      ) : isPlatformDisabled ? (
                        <Badge
                          variant="outline"
                          className="bg-zinc-100 text-zinc-600 border-zinc-300"
                          data-testid={`disabled-${card.platform}`}
                        >
                          Disabled
                        </Badge>
                      ) : meta.coming_soon ? (
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                          Coming soon
                        </Badge>
                      ) : !card.configured ? (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          <AlertCircle className="w-3 h-3 mr-1" /> Not configured
                        </Badge>
                      ) : (
                        <Badge variant="outline">Disconnected</Badge>
                      )}
                    </div>
                    <CardTitle className="text-base mt-3">
                      {meta.label}
                      {isMulti && card.account_name ? (
                        <span className="ml-1 text-xs font-normal text-zinc-500">
                          · {card.account_name}
                        </span>
                      ) : null}
                      {card.connected && card.platform === 'instagram' && (
                        <Badge
                          variant="outline"
                          data-testid="ig-provider-badge"
                          className={`ml-2 text-[10px] align-middle ${
                            card.provider === 'instagram_login'
                              ? 'bg-pink-50 text-pink-700 border-pink-200'
                              : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}
                        >
                          {card.provider === 'instagram_login' ? 'Direct' : 'via Facebook'}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {card.connected
                        ? `Connected as ${card.account_name || 'your account'}${
                            isMulti && (card.pages || []).length
                              ? ` · ${card.pages.length} ${card.platform === 'instagram' ? 'IG account' : 'page'}${card.pages.length === 1 ? '' : 's'}`
                              : ''
                          }`
                        : meta.desc}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {card.connected ? (
                      <div className="space-y-2">
                        {showFbDefault && (
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-zinc-600">Default Page</label>
                            <Select
                              value={fbSelected || ''}
                              onValueChange={handleSelectFbPage}
                              disabled={fbLoading}
                            >
                              <SelectTrigger className="w-full" data-testid="fb-default-page-select">
                                <SelectValue placeholder={fbLoading ? 'Loading…' : 'Select a Page'} />
                              </SelectTrigger>
                              <SelectContent>
                                {fbPages.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    <span className="flex items-center gap-2">
                                      {p.name}
                                      {p.has_instagram && (
                                        <span className="text-[10px] text-pink-600 bg-pink-50 px-1 rounded">+IG</span>
                                      )}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-[10px] text-zinc-500">
                              All Facebook posts will publish to this Page.
                            </p>
                          </div>
                        )}
                        {showLiTarget && (
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-zinc-600">Posting target</label>
                            <Select
                              value={liSelected || 'personal'}
                              onValueChange={handleSelectLiTarget}
                              disabled={liLoading}
                            >
                              <SelectTrigger className="w-full" data-testid="li-target-select">
                                <SelectValue placeholder={liLoading ? 'Loading…' : 'Select target'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="personal">
                                  <span className="flex items-center gap-2">
                                    <span>Personal profile</span>
                                    <span className="text-[10px] text-zinc-500 bg-zinc-100 px-1 rounded">{card.account_name || 'You'}</span>
                                  </span>
                                </SelectItem>
                                {liOrgs.map((o) => (
                                  <SelectItem key={o.urn} value={o.urn}>
                                    <span className="flex items-center gap-2">
                                      <span>{o.name}</span>
                                      <span className="text-[10px] text-sky-700 bg-sky-50 px-1 rounded">Company Page</span>
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-[10px] text-zinc-500">
                              {liHint ? liHint : (liOrgs.length > 0
                                ? 'All LinkedIn posts will publish to the selected target.'
                                : 'Connect with org admin scopes to enable Company Page posting.')}
                            </p>
                          </div>
                        )}
                        {/* Multi-instance: show the pages this connection owns */}
                        {isMulti && (card.pages || []).length > 0 && (
                          <div className="text-[11px] text-zinc-500 leading-snug">
                            {(card.pages || []).slice(0, 3).map((p) => (
                              <div key={p.id} className="truncate">• {p.name || p.ig_username || p.id}</div>
                            ))}
                            {(card.pages || []).length > 3 && (
                              <div>+ {(card.pages || []).length - 3} more</div>
                            )}
                          </div>
                        )}
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => testConnection(card.platform)}
                          disabled={busy === card.platform}
                          data-testid={`test-connection-${card.platform}${card._isInstance && card.connection_id ? `-${card.connection_id}` : ''}`}
                        >
                          {busy === card.platform ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Activity className="w-4 h-4 mr-2" />
                          )}
                          Test connection
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => disconnect(
                            card.platform,
                            card._isInstance ? card.connection_id : null,
                            card.account_name,
                          )}
                          data-testid={`disconnect-${card.platform}${card._isInstance && card.connection_id ? `-${card.connection_id}` : ''}`}
                        >
                          <Unplug className="w-4 h-4 mr-2" />
                          Disconnect
                        </Button>
                      </div>
                    ) : meta.coming_soon ? (
                      <Button
                        className="w-full"
                        variant="outline"
                        disabled
                        data-testid={`connect-${card.platform}`}
                      >
                        Coming soon
                      </Button>
                    ) : greyedOut ? (
                      <Button
                        className="w-full"
                        variant="outline"
                        disabled
                        data-testid={`connect-${card.platform}`}
                      >
                        {isPending ? 'Pending platform approval' : 'Currently unavailable'}
                      </Button>
                    ) : (
                      <>
                        {adminCanBypass && (
                          <div
                            className="mb-2 text-[11px] px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800"
                            data-testid={`admin-test-hint-${card.platform}`}
                          >
                            Admin testing access — this platform is {isPending ? 'pending approval' : 'disabled'} for end-users.
                          </div>
                        )}
                        <Button
                          className="w-full bg-indigo-600 hover:bg-indigo-700"
                          disabled={!card.configured || busy === card.platform}
                          onClick={() => startConnect(card.platform)}
                          data-testid={`connect-${card.platform}`}
                        >
                          {busy === card.platform ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <ExternalLink className="w-4 h-4 mr-2" />
                          )}
                          {card.configured ? 'Connect' : 'Awaiting platform owner'}
                        </Button>
                        {card.platform === 'instagram' && (
                          <Button
                            variant="outline"
                            className="w-full mt-2 border-pink-200 text-pink-700 hover:bg-pink-50"
                            disabled={busy === 'instagram-direct'}
                            onClick={() => startConnect('instagram-direct')}
                            data-testid="connect-instagram-direct"
                          >
                            {busy === 'instagram-direct' ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Instagram className="w-4 h-4 mr-2" />
                            )}
                            Connect directly (no Facebook)
                          </Button>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            });

            // For multi-instance platforms with at least one connection, show
            // a small dashed "Connect another …" tile so the user can add more
            // accounts without disconnecting the existing ones.
            if (isMulti && instances.length > 0 && c.configured) {
              cards.push(
                <Card
                  key={`${c.platform}-add-another`}
                  className="border-dashed border-zinc-300 bg-zinc-50 hover:bg-white transition-colors"
                  data-testid={`connect-another-${c.platform}`}
                >
                  <CardContent className="p-6 flex flex-col items-center justify-center text-center min-h-[180px]">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${meta.accent} mb-3`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-medium text-zinc-700">
                      Connect another {meta.label} account
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-1 max-w-[220px]">
                      Sign in with a different account. Existing connections stay intact.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => startConnect(c.platform)}
                      disabled={busy === c.platform}
                      data-testid={`connect-another-${c.platform}-btn`}
                    >
                      {busy === c.platform ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                      ) : (
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Connect another
                    </Button>
                    {c.platform === 'instagram' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-pink-700 hover:bg-pink-50"
                        onClick={() => startConnect('instagram-direct')}
                        disabled={busy === 'instagram-direct'}
                        data-testid="connect-instagram-direct-another"
                      >
                        {busy === 'instagram-direct' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Instagram className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Connect directly (no Facebook)
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            }
            return cards;
          })}
        </div>
      )}

      {/* Bluesky connect modal — handle + app password (no OAuth redirect) */}
      {bskyModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="bluesky-connect-modal"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Connect Bluesky</h2>
              <p className="text-sm text-zinc-600 mt-1">
                Bluesky uses an "app password" instead of OAuth.
                Generate one at{' '}
                <a
                  href="https://bsky.app/settings/app-passwords"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 underline"
                >
                  bsky.app/settings/app-passwords
                </a>
                . Don't use your account password.
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-zinc-700">Handle</label>
                <input
                  type="text"
                  value={bskyHandle}
                  onChange={(e) => setBskyHandle(e.target.value)}
                  placeholder="yourname.bsky.social"
                  autoFocus
                  className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded-md text-sm"
                  data-testid="bluesky-handle-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700">App password</label>
                <input
                  type="password"
                  value={bskyPassword}
                  onChange={(e) => setBskyPassword(e.target.value)}
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono"
                  data-testid="bluesky-password-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setBskyModal(false)}
                disabled={bskySaving}
              >
                Cancel
              </Button>
              <Button
                onClick={submitBlueskyConnect}
                disabled={bskySaving || !bskyHandle.trim() || !bskyPassword.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="bluesky-connect-submit"
              >
                {bskySaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Connect
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionsPage;
